import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  DeprecatedButtonGroup,
  ComponentTextSmall,
  HeadingText20,
  HeadingText50,
  Icon,
} from '@modernhealth/vital-components';
import {
  CircularProgress,
  ListSubheader,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import AutoComplete from '@material-ui/lab/Autocomplete';

import { ServiceType } from 'src/generated/webapp_gql';

import type { InvoicingPageStepProps } from '../types';
import { InfoCallout } from '../components/InfoCallout';
import { BillingCodesInfoBubble } from '../../Clients/BillingCodesInfoBubble';
import { SimplifiedCptCodeNodeFragment } from '../documents/SimplifiedCptCodeNode.generated';
import { SimplifiedDxCodeNodeFragment } from '../documents/SimplifiedDxCodeNode.generated';
import { GetDxCodesDocument, GetDxCodesQuery } from '../documents/getDxCodes.generated';
import {
  GetCptCodesByServiceTypeDocument,
  GetCptCodesByServiceTypeQuery,
  GetCptCodesByServiceTypeQueryVariables,
} from '../documents/getCptCodesByServiceType.generated';

export enum BillingDetailsTestID {
  Container = 'BillingDetailsTestID.Container',
  CPTCodeSelector = 'BillingDetailsTestID.CPTCodeSelector',
  DiagnosisCodeSelector = 'BillingDetailsTestID.DiagnosisCodeSelector',
  AddSecondaryDiagnosisCode = 'BillingDetailsTestID.AddSecondaryDiagnosisCode',
  DeleteSecondaryDiagnosisCode = 'BillingDetailsTestID.DeleteSecondaryDiagnosisCode',
  BackButton = 'BillingDetailsTestID.BackButton',
  NextButton = 'BillingDetailsTestID.NextButton',
}

export const BillingDetails: React.FC<InvoicingPageStepProps> = ({
  data,
  setData,
  prevStep,
  nextStep,
  memberClient,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const styles = useStyles();

  const [cptCodes, setCptCodes] = useState<SimplifiedCptCodeNodeFragment[]>([]);
  const [dxCodes, setDxCodes] = useState<SimplifiedDxCodeNodeFragment[]>([]);

  const canContinue = useMemo<boolean>(
    () => Boolean(data.cptCode) && data.diagnosisCodes.every(Boolean),
    [data],
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { data: cptCodesData } = await memberClient.query<
        GetCptCodesByServiceTypeQuery,
        GetCptCodesByServiceTypeQueryVariables
      >({
        query: GetCptCodesByServiceTypeDocument,
        variables: {
          serviceType: ServiceType.Psychiatry,
        },
      });

      const { data: dxCodesData } = await memberClient.query<GetDxCodesQuery>({
        query: GetDxCodesDocument,
      });

      setCptCodes((cptCodesData.getCptCodesByServiceType as SimplifiedCptCodeNodeFragment[]) ?? []);
      setDxCodes((dxCodesData.getDxCodes as SimplifiedDxCodeNodeFragment[]) ?? []);
      setIsLoading(false);
    })();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <CircularProgress />;
      </div>
    );
  }

  return (
    <div data-testid={BillingDetailsTestID.Container}>
      <HeadingText50>Billing Details</HeadingText50>
      <div className={styles.form}>
        <InfoCallout data={data} />
        <div className={styles.formField}>
          <div className="flex items-center">
            <HeadingText20 className="m-0 mr-2">CPT code</HeadingText20>
            <BillingCodesInfoBubble />
          </div>
          <AutoComplete
            options={cptCodes}
            groupBy={(cptCode) => cptCode!.groupName!}
            getOptionLabel={(cptCode) => `${cptCode!.code!} • ${cptCode!.description!}`}
            renderInput={(props) => (
              <TextField {...props} variant="outlined" placeholder="Select a CPT code" />
            )}
            renderGroup={({ key, group, children }) => (
              <Fragment key={key}>
                <ListSubheader>
                  {group === 'MOST_COMMON' ? 'Most common' : 'Additional'} codes
                </ListSubheader>
                {children}
              </Fragment>
            )}
            value={cptCodes.find((cptCode) => cptCode.code === data.cptCode) ?? null}
            onChange={(_, cptCode) => {
              if (!cptCode) return;
              setData((data) => ({ ...data, cptCode: cptCode!.code! }));
            }}
          />
        </div>
        <div className={styles.formField}>
          <div className="flex items-center">
            <HeadingText20 className="m-0 mr-2">Diagnosis code</HeadingText20>
            <BillingCodesInfoBubble />
          </div>
          {data.diagnosisCodes.map((code, codeIndex) => (
            <div key={`diagnosisCode-${codeIndex}`} className="flex items-center">
              <div className="flex-auto">
                <AutoComplete
                  options={dxCodes.filter((dxCode) => !data.diagnosisCodes.includes(dxCode!.code!))}
                  getOptionLabel={(dxCode) => `${dxCode.code} • ${dxCode.description}`}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      variant="outlined"
                      placeholder={`Search for a ${
                        codeIndex === 0 ? 'primary' : 'secondary'
                      } diagnosis code`}
                    />
                  )}
                  renderOption={(dxCode) => {
                    return (
                      <div className="flex flex-col gap-1">
                        <Typography variant="subtitle1">
                          {dxCode.code} &bull; {dxCode.description}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {dxCode.primaryGroup} | {dxCode.secondaryGroup}
                        </Typography>
                      </div>
                    );
                  }}
                  value={
                    dxCodes.find((dxCode) => dxCode.code === data.diagnosisCodes[codeIndex]) ?? null
                  }
                  onChange={(_, dxCode) => {
                    if (!dxCode) return;
                    setData((data) => ({
                      ...data,
                      diagnosisCodes: [
                        ...data.diagnosisCodes.slice(0, codeIndex),
                        dxCode!.code!,
                        ...data.diagnosisCodes.slice(codeIndex + 1),
                      ],
                    }));
                  }}
                />
              </div>
              {codeIndex !== 0 ? (
                <button
                  className="ml-2"
                  data-testid={`${BillingDetailsTestID.DeleteSecondaryDiagnosisCode}${codeIndex}`}
                  title="Delete secondary code"
                  onClick={() =>
                    setData((data) => ({
                      ...data,
                      diagnosisCodes: data.diagnosisCodes.filter((_, cx) => cx !== codeIndex),
                    }))
                  }
                >
                  <Icon size={16} name="minus-enclosed" color="blue55" />
                </button>
              ) : null}
            </div>
          ))}
        </div>
        <button
          disabled={!data.diagnosisCodes.every(Boolean)}
          className="flex items-center"
          data-testid={BillingDetailsTestID.AddSecondaryDiagnosisCode}
          onClick={() =>
            setData((data) => ({ ...data, diagnosisCodes: [...data.diagnosisCodes, null] }))
          }
        >
          <Icon
            size={16}
            name="plus-enclosed"
            color={data.diagnosisCodes.every(Boolean) ? 'blue55' : 'gray20'}
            className="mr-2"
          />
          <ComponentTextSmall fontColor={data.diagnosisCodes.every(Boolean) ? 'blue55' : 'gray20'}>
            Add a secondary diagnosis code
          </ComponentTextSmall>
        </button>
      </div>

      <DeprecatedButtonGroup
        firstButtonText="Back"
        firstButtonTestId={BillingDetailsTestID.BackButton}
        onFirstButtonClick={prevStep}
        secondButtonText="Next"
        secondButtonTestId={BillingDetailsTestID.NextButton}
        onSecondButtonClick={nextStep}
        secondButtonDisabled={!canContinue}
      />
    </div>
  );
};

const useStyles = makeStyles(() => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    '& > *': {
      margin: 0,
    },
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    '& > *': {
      margin: 0,
    },
  },
}));
